import {createFeatureSelector, createSelector} from '@ngrx/store';
import {DelegoActions, DelegoActionTypes} from '../actions/delego.action';
import {merge} from 'lodash-es';
import {
  AuthorizationResult,
  delegoInitialState,
  DelegoPaymentState,
  PAYMENT_AUTHORIZATION_FEATURE,
} from '../payment-state';
import {DelegoRapidPayThreeDSecureInfoWithDelegoToken} from '@app/model/delego-rapidpay/delego-rapidpay-authenticate';
import {
  DelegoRapidPayThreeDSecureStatus,
  DelegoRapidPayThreeDSecureStatusReason,
} from '@app/model/delego-rapidpay/delego-rapidpay.enum';
import {defaultDelegoAuthenticationValidStatuses} from '../../delego-rapidpay/validation/default-delego-authentication-validation.service';
import {DelegoAuthenticationValidations} from '../../delego-rapidpay/validation/delego-athentication-validations.model';

const authenticationValidationStrategies: DelegoAuthenticationValidations = {
  /**
   * AMEX credit card authentication considered as valid for status "U"(UnableToComplete)
   * if eci is "07" AND statusReason is "01"(CARD_AUTHENTICATION_FAILED) or statusReason is "80"
   */
  amex: {
    isValid: (authResponse: DelegoRapidPayThreeDSecureInfoWithDelegoToken) =>
      authenticationValidationStrategies.default.isValid(authResponse) ||
      (authResponse?.status === DelegoRapidPayThreeDSecureStatus.UnableToComplete &&
        authResponse?.eci === '07' &&
        (authResponse?.statusReason === DelegoRapidPayThreeDSecureStatusReason.CARD_AUTHENTICATION_FAILED ||
          authResponse?.statusReason ===
            DelegoRapidPayThreeDSecureStatusReason.CARD_AUTHENTICATION_FAILED_AMEX_PLASTICLESS)),
  },
  default: {
    isValid: (authResponse: DelegoRapidPayThreeDSecureInfoWithDelegoToken) =>
      defaultDelegoAuthenticationValidStatuses.includes(authResponse?.status),
  },
};

const getDelegoFeatureState = createFeatureSelector<{
  delego: DelegoPaymentState;
  stripe: DelegoPaymentState;
}>(PAYMENT_AUTHORIZATION_FEATURE);

export const getState = createSelector(getDelegoFeatureState, (state) => state.delego);

export const delegoReducer = (state = delegoInitialState, action: DelegoActions): DelegoPaymentState => {
  switch (action.type) {
    case DelegoActionTypes.AuthorizeCart:
      return {
        ...state,
        start: true,
        cardType: action.payload.cardType,
        error: false,
        data: undefined,
      };
    case DelegoActionTypes.AuthorizationInProcess:
      return {
        ...state,
        start: false,
        loading: true,
      };
    case DelegoActionTypes.AuthorizationSetup:
      const stateCopy = {
        ...state,
        loading: true,
        cartId: action.payload.cartCode,
        consignmentCodes: action.payload.consignments.map((consignment) => consignment.code),
        data: {},
      };
      for (const consignment of action.payload.consignments) {
        stateCopy.data[consignment.code] = prepareInitialAuthorizationState(consignment.code);
      }
      return stateCopy;
    case DelegoActionTypes.AuthenticationNotRequired: {
      return merge({}, state, {
        data: {
          [action.payload.consignmentCode]: {
            authenticationNotRequired: action.payload.authenticationNotRequired,
          },
        },
      });
    }
    case DelegoActionTypes.SetAuthorizationSuccess:
    case DelegoActionTypes.SetAuthorizationFailure: {
      const newState = merge({}, state, {
        data: {
          [action.payload.consignmentCode]: {
            delegoAuthorizationResponse: action.payload.authorizationResult,
          },
        },
      });
      return setLoadingToFalseIfAuthorizationCompleted(newState);
    }
    case DelegoActionTypes.SetAuthenticationSuccess:
    case DelegoActionTypes.SetAuthenticationFailure: {
      const payload = action.payload;
      const newState: DelegoPaymentState = merge({}, state, {
        data: {
          [payload.consignmentCode]: {
            delegoAuthenticationResponse: payload.authenticationResult,
          },
        },
      });
      return setLoadingToFalseIfAuthorizationCompleted(newState);
    }
    case DelegoActionTypes.SetMerchantIdSuccess: {
      const newState = merge({}, state, {
        data: {
          [action.payload.consignmentCode]: {
            merchantId: action.payload.merchantId,
          },
        },
      });
      return setLoadingToFalseIfAuthorizationCompleted(newState);
    }
    case DelegoActionTypes.AuthorizationNotRequired:
      return {
        ...state,
        loading: false,
        start: false,
        consignmentCodes: [],
        data: undefined,
      };
    case DelegoActionTypes.ErrorOccurred:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    default:
      return state;
  }
};

const prepareInitialAuthorizationState = (code: string): AuthorizationResult => ({
  code,
  merchantId: undefined,
});

/**
 * Set 'loading' flag to false if authorization is finished
 */
const setLoadingToFalseIfAuthorizationCompleted = (state: DelegoPaymentState): DelegoPaymentState => {
  let isFinished = true;
  for (const consignmentCode of state.consignmentCodes) {
    const entityState: AuthorizationResult = state.data[consignmentCode];
    if (entityState.authenticationNotRequired) {
      if (!entityState.delegoAuthorizationResponse) {
        isFinished = false;
        break;
      }
    } else {
      if (!entityState.delegoAuthenticationResponse) {
        isFinished = false;
        break;
      }
      if (
        isAuthenticationSuccess(entityState.delegoAuthenticationResponse, state.cardType) &&
        !entityState.delegoAuthorizationResponse
      ) {
        isFinished = false;
        break;
      }
    }
  }
  if (isFinished) {
    state.loading = false;
  }
  return state;
};

const isAuthenticationSuccess = (authResponse: DelegoRapidPayThreeDSecureInfoWithDelegoToken, cardType: string) => {
  const authenticationSuccessStrategy =
    authenticationValidationStrategies[cardType?.toLowerCase()] || authenticationValidationStrategies.default;
  return authenticationSuccessStrategy.isValid(authResponse);
};
